import { apiRequest } from "./client";

export type UserRole = "admin" | "operator" | "viewer";
export type Platform = "ios" | "android";
export type RolloutType = "all" | "percentage" | "target_users";
export type AppUpdateQuickAction =
  | "set_all"
  | "set_percentage"
  | "target_users"
  | "disable";
export type FeatureFlagAudienceType =
  | "none"
  | "all"
  | "percentage"
  | "target_users";
export type DeviceStatus = "active" | "invalid" | "revoked";
export type OperationalActorType = "admin" | "public" | "internal" | "system";
export type OperationalSeverity = "info" | "warning" | "error";
export type DeliveryStatus =
  | "delivered"
  | "failed"
  | "pending"
  | "invalid_token";
export type PushPermissionState = "granted" | "denied" | "unknown";
export type PushCampaignStatus = "draft" | "dry_run_ready" | "sent" | "failed";
export type AirbridgeTrackingLinkStatus =
  | "PENDING"
  | "READY"
  | "FAILED"
  | "UNKNOWN"
  | "STALE";

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  createdAt: string;
}

export interface AdminUser extends AuthUser {
  fullName: string;
  isActive: boolean;
  deletedAt: string | null;
  updatedAt: string;
  mfaEnabled: boolean;
  mfaEnabledAt: string | null;
  recoveryCodesRemaining: number;
}

export interface AdminUserSession {
  id: string;
  userId: string;
  email: string;
  role: UserRole;
  createdAt: string;
  expiresAt: string;
  current: boolean;
}

export interface AdminSessionResponse {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  createdAt: string;
}

export type MfaLoginMethod = "totp" | "recovery_code";

export interface AuthenticatedLoginResponse {
  status: "authenticated";
  session: AdminSessionResponse;
}

export interface MfaRequiredLoginResponse {
  status: "mfa_required";
  challengeId: string;
  methods: MfaLoginMethod[];
  expiresInSeconds: number;
}

export interface MfaSetupRequiredLoginResponse {
  status: "mfa_setup_required";
  challengeId: string;
  otpauthUrl: string;
  manualKey: string;
  expiresInSeconds: number;
}

export type LoginResponse =
  | AuthenticatedLoginResponse
  | MfaRequiredLoginResponse
  | MfaSetupRequiredLoginResponse;

export interface MfaVerifyResponse {
  status: "authenticated";
  session: AdminSessionResponse;
  recoveryCodesRemaining: number | null;
}

export interface MfaSetupConfirmResponse {
  status: "authenticated";
  session: AdminSessionResponse;
  recoveryCodes: string[];
}

export interface AppUpdateConfig {
  id: string;
  platform: Platform;
  channel: string;
  latestVersion: string;
  minVersion: string;
  changelog: string;
  appStoreUrl: string;
  rolloutType: RolloutType;
  rolloutPercentage: number | null;
  targetCustomerIds: string[];
  enabled: boolean;
  updatedAt: string;
  updatedBy: string;
  checks24h: number;
  rolloutHitRate: number;
}

export interface AppUpdatePreviewResult {
  available: boolean;
  forceUpdate: boolean;
  reason: string;
  latestVersion: string | null;
  minVersion: string | null;
  appStoreUrl: string;
  changelog: string;
}

export interface AppUpdateQuickActionPayload {
  action: AppUpdateQuickAction;
  rolloutPercentage?: number | null;
  targetCustomerIds?: string[];
}

export interface AppReleaseVersionBucket {
  version: string;
  count: number;
}

export interface AppReleasePlatformSummary {
  platform: Platform;
  configId: string | null;
  enabled: boolean;
  channel: string;
  latestVersion: string | null;
  minVersion: string | null;
  rolloutType: RolloutType | null;
  rolloutPercentage: number | null;
  targetUsersCount: number;
  storeUrlReady: boolean;
  checks24h: number;
  checks7d: number;
  updateRequired24h: number;
  forcedUpdate24h: number;
  rolloutHitRate24h: number;
  activeDevices: number;
  staleDevices: number;
  belowMinDevices: number;
  versionDistribution: AppReleaseVersionBucket[];
}

export interface AppReleaseRecentEvent {
  id: string;
  eventType: string;
  message: string;
  actor: string;
  platform: Platform | null;
  at: string;
}

export interface AppReleaseSummary {
  generatedAt: string;
  platforms: AppReleasePlatformSummary[];
  recentEvents: AppReleaseRecentEvent[];
}

export interface PaymentMethodConfig {
  id: string;
  key: string;
  method: string | null;
  title: string;
  description: string;
  disabledReason: string;
  sortOrder: number;
  enabled: boolean;
  updatedAt: string;
  updatedBy: string;
}

export type PaymentMethodConfigPayload = Omit<
  PaymentMethodConfig,
  "id" | "updatedAt" | "updatedBy"
>;

export interface FeatureFlagConfig {
  id: string;
  key: string;
  title: string;
  description: string;
  owner: string;
  category: string;
  enabled: boolean;
  audienceType: FeatureFlagAudienceType;
  rolloutPercentage: number | null;
  targetCustomerIds: string[];
  startsAt: string | null;
  endsAt: string | null;
  expiresAt: string | null;
  defaultEnabled: boolean;
  payload: Record<string, unknown>;
  metadata: Record<string, unknown>;
  killSwitchPriority: number;
  updatedAt: string;
  updatedBy: string;
}

export type FeatureFlagConfigPayload = Omit<
  FeatureFlagConfig,
  "id" | "updatedAt" | "updatedBy"
>;

export interface FeatureFlagPreviewResult {
  key: string;
  enabled: boolean;
  visible: boolean;
  reason: string;
  payload: Record<string, unknown>;
  evaluatedAt: string;
  source: string;
}

export interface BuyerServiceFaqItem {
  id: string;
  question: string;
  answer: string;
  enabled: boolean;
  sortOrder: number;
  updatedAt: string;
  updatedBy: string;
}

export type BuyerServiceFaqItemPayload = Omit<
  BuyerServiceFaqItem,
  "id" | "updatedAt" | "updatedBy"
>;

export interface DeviceInstallation {
  installationId: string;
  deviceId: string;
  customerId: string;
  platform: Platform;
  appVersion: string;
  buildNumber: string;
  pushPermission: boolean;
  status: DeviceStatus;
  lastSeen: string;
  registeredAt: string;
  invalidatedAt: string | null;
  invalidReason: string | null;
  maskedPushToken: string;
  tokenHash: string;
}

export interface PushPreviewResult {
  count: number;
  ios: number;
  android: number;
  statuses: Record<string, number>;
}

export interface PushTestResult {
  success: boolean;
  totalDevices: number;
  delivered: number;
  failed: number;
  invalidToken: number;
}

export interface PushCampaignFilters {
  platform: Platform | null;
  appVersion: string | null;
  pushPermission: PushPermissionState | null;
  activeOnly: boolean;
}

export interface PushCampaignPayload {
  title: string;
  body: string;
  eventType: string;
  data: Record<string, unknown>;
  targetFilters: PushCampaignFilters;
  targetCustomerIds: string[];
}

export interface PushCampaignDryRunResult {
  count: number;
  ios: number;
  android: number;
  statuses: Record<string, number>;
  sample: Array<Record<string, unknown>>;
  truncated: boolean;
  limit: number;
}

export interface PushCampaign {
  id: string;
  title: string;
  body: string;
  eventType: string;
  data: Record<string, unknown>;
  targetFilters: PushCampaignFilters;
  targetCustomerIds: string[];
  status: PushCampaignStatus;
  dryRunCount: number;
  dryRunIos: number;
  dryRunAndroid: number;
  dryRunStatuses: Record<string, number>;
  dryRunSample: Array<Record<string, unknown>>;
  dryRunTruncated: boolean;
  dryRunAt: string | null;
  sendTotalDevices: number;
  sendDelivered: number;
  sendFailed: number;
  sendInvalidToken: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  sentBy: string | null;
  sentAt: string | null;
  sendLimit: number;
}

export interface PushCampaignSendResult {
  campaign: PushCampaign;
  success: boolean;
  totalDevices: number;
  delivered: number;
  failed: number;
  invalidToken: number;
}

export interface DeliveryLog {
  id: string;
  campaignId?: string | null;
  createdAt: string;
  eventType: string;
  customerId: string | null;
  platform: Platform;
  status: DeliveryStatus;
  provider: string;
  errorCode: string | null;
  errorMessage?: string | null;
  deviceId?: string | null;
}

export interface TimelineEvent {
  id: string;
  eventType: string;
  actorType: OperationalActorType | string;
  actorId: string | null;
  actorLabel: string;
  endpoint: string | null;
  resourceType: string | null;
  resourceId: string | null;
  customerId: string | null;
  deviceId: string | null;
  platform: Platform | null;
  severity: OperationalSeverity | string;
  statusCode: number | null;
  isPositive: boolean | null;
  message: string;
  payload: Record<string, unknown>;
  createdAt: string;
}

export interface TimelineSearchResponse {
  items: TimelineEvent[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface AirbridgeTrackingLink {
  id: string;
  status: AirbridgeTrackingLinkStatus;
  fingerprint: string;
  environment: string;
  airbridgeAppName: string;
  payloadSchemaVersion: number;
  airbridgeId: number | null;
  shortId: string | null;
  shortUrl: string | null;
  primaryUrl: string | null;
  clickUrl: string | null;
  impressionUrl: string | null;
  serverToServerClickUrl: string | null;
  trackingTemplateId: string | null;
  channelType: string | null;
  sourceService: string | null;
  createdByKeyId: string | null;
  callerReference: string | null;
  requestCorrelationId: string | null;
  attemptCount: number;
  lastAirbridgeStatusCode: number | null;
  failureKind: string | null;
  lastError: string | null;
  channel: string | null;
  campaign: string | null;
  adGroup: string | null;
  adCreative: string | null;
  deeplinkUrl: string | null;
  fallbackPaths: Record<string, unknown>;
  normalizedPayload: Record<string, unknown>;
  airbridgeResponse: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface AirbridgeTrackingLinkSearchResponse {
  items: AirbridgeTrackingLink[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface AirbridgeTrackingLinkFilters {
  status?: AirbridgeTrackingLinkStatus | "";
  sourceService?: string;
  channel?: string;
  search?: string;
  createdFrom?: string;
  createdTo?: string;
  limit?: number;
  offset?: number;
}

export type AirbridgeTrackingLinkCopyField =
  | "primaryUrl"
  | "shortUrl"
  | "clickUrl"
  | "impressionUrl"
  | "serverToServerClickUrl"
  | "fingerprint"
  | "airbridgeId"
  | "trackingTemplateId";

export interface TimelineFilters {
  eventType?: string;
  actorType?: OperationalActorType | "";
  actorId?: string;
  severity?: OperationalSeverity | "";
  platform?: Platform | "";
  customerId?: string;
  deviceId?: string;
  resourceType?: string;
  resourceId?: string;
  createdFrom?: string;
  createdTo?: string;
  limit?: number;
  offset?: number;
}

export interface DashboardPayload {
  kpiCards: {
    activeDevices: number;
    invalidTokens: number;
    pushSent24h: number;
    pushFailures24h: number;
    activeRollouts: number;
    appUpdateChecks24h: number;
    deliverySuccessRate24h: number;
  };
  appUpdateSnapshot: Array<{
    id: string;
    platform: Platform;
    channel: string;
    latestVersion: string;
    minVersion: string;
    rolloutMode: RolloutType;
    rolloutValue: number | null;
    enabled: boolean;
  }>;
  deviceHealthSnapshot: {
    seenToday: number;
    byPlatform: { ios: number; android: number };
    invalidInstallations: number;
    revokedInstallations: number;
    versionDistribution: Array<{ version: string; count: number }>;
  };
  pushSnapshot: {
    sends24h: number;
    failures24h: number;
    invalidTokenEvents24h: number;
    successRate24h: number;
    successRate7d: number;
  };
  appUpdateChecksTrend7d: Array<{ date: string; checks: number }>;
  pushTrend7d: Array<{ date: string; sends: number; failures: number }>;
  devicesSeenTrend7d: Array<{ date: string; devices: number }>;
  recentEvents: Array<{
    id: string;
    type: string;
    message: string;
    actor: string;
    at: string;
    severity: "info" | "warning" | "error";
  }>;
}

export type DashboardOperationSeverity = "info" | "warning" | "error";

export interface DashboardOperationCard {
  id: string;
  title: string;
  value: string | number;
  detail: string;
  severity: DashboardOperationSeverity;
  href: string;
  trendDelta: number;
}

export interface DashboardOperationAlert {
  id: string;
  severity: DashboardOperationSeverity;
  title: string;
  message: string;
  href: string;
  metricLabel: string;
  metricValue: string | number;
}

export interface DashboardOperationsPayload {
  generatedAt: string;
  cards: DashboardOperationCard[];
  alerts: DashboardOperationAlert[];
  links: Array<{ href: string; count: number }>;
  recentEvents: DashboardPayload["recentEvents"];
}

export interface DevicesAnalyticsPayload {
  byPlatformData: Array<{ name: string; value: number; color: string }>;
  appVersionsData: Array<{ version: string; count: number }>;
  seenTrend7d: Array<{ date: string; count: number }>;
}

export interface PushAnalyticsPayload {
  pushSendsTrend7d: Array<{ date: string; sends: number; failures: number }>;
  successRateData: Array<{ date: string; rate: number }>;
  invalidTokenReasons: Array<{ reason: string; count: number }>;
  recentFailures: DeliveryLog[];
}

export interface SystemPayload {
  serviceHealth: Array<{
    name: string;
    status: "ok" | "degraded" | "down";
    latencyMs: number | null;
    message: string;
    checkedAt: string;
    checkMode: "runtime" | "probe" | "configuration";
  }>;
  runtimeInfo: {
    startedAt: string;
    uptimeHours: number;
  };
  requestVolume24h: Array<{ hour: string; count: number }>;
  endpointErrors: Array<{ endpoint: string; errors: number; p99ms: number }>;
  adminActions24h: Array<{
    time: string;
    actor: string;
    action: string;
    resource: string;
  }>;
}

export interface SupportTraceSummary {
  trace_id: string;
  first_seen_at: string;
  last_seen_at: string;
  customer_id: string;
  source: string;
  session_id: string;
  lead_id: number | null;
  final_stage: string;
  final_decision: string;
  final_outcome: string;
  events_count: number;
}

export interface SupportTraceEvent {
  timestamp: string;
  trace_id: string;
  span_id: string;
  parent_span_id: string;
  customer_id: string;
  source: string;
  session_id: string;
  lead_id: number | null;
  conversation_id: string;
  message_id: string;
  stage: string;
  decision: string;
  outcome: string;
  reason: string;
  duration_ms: number;
  state_before: Record<string, unknown>;
  state_after: Record<string, unknown>;
  details: Record<string, unknown>;
  error_type: string;
  error_message: string;
}

export interface SupportTraceHumanSummary {
  headline: string;
  answered_by: string;
  message_direction: string;
  delivery_target: string;
  delivery_status: string;
  decision_reason: string;
  customer_id: string;
  source: string;
  session_id: string;
  conversation_id: string;
  lead_id: number | null;
  chat_id: string | null;
  support_conversation_status: string;
  conversation_owner: string;
  amo_deal_status: string;
  severity: string;
}

export interface SupportTraceHumanStep {
  timestamp: string;
  title: string;
  description: string;
  actor: string;
  target: string;
  outcome: string;
  stage: string;
  decision: string;
  facts: string[];
}

export interface SupportTracesResponse {
  total: number;
  offset: number;
  limit: number;
  traces: SupportTraceSummary[];
}

export interface SupportTraceDetailResponse {
  trace_id: string;
  summary: SupportTraceHumanSummary;
  steps: SupportTraceHumanStep[];
  events: SupportTraceEvent[];
}

export interface SupportTraceAnalyticsItem {
  stage: string;
  decision: string;
  outcome: string;
  count: number;
  p95_duration_ms: number;
}

export interface SupportTraceAnalyticsResponse {
  items: SupportTraceAnalyticsItem[];
}

export interface SupportTraceFilters {
  customerId?: string;
  sessionId?: string;
  leadId?: string;
  source?: string;
  stage?: string;
  decision?: string;
  outcome?: string;
  from?: string;
  to?: string;
  offset?: number;
  limit?: number;
}

export async function login(
  email: string,
  password: string,
): Promise<LoginResponse> {
  const response = await apiRequest<LoginResponse | { session: AdminSessionResponse }>(
    "/api/v1/admin/auth/login",
    {
      method: "POST",
      body: { email, password },
    },
  );
  if ("status" in response) {
    return response;
  }
  return { status: "authenticated", session: response.session };
}

export async function getCurrentSession(): Promise<AdminSessionResponse> {
  return apiRequest<AdminSessionResponse>("/api/v1/admin/auth/me");
}

export async function verifyMfaLogin(data: {
  challengeId: string;
  method: MfaLoginMethod;
  code: string;
}): Promise<MfaVerifyResponse> {
  return apiRequest<MfaVerifyResponse>("/api/v1/admin/auth/mfa/verify", {
    method: "POST",
    body: data,
  });
}

export async function confirmMfaSetup(data: {
  challengeId: string;
  code: string;
}): Promise<MfaSetupConfirmResponse> {
  return apiRequest<MfaSetupConfirmResponse>("/api/v1/admin/auth/mfa/setup/confirm", {
    method: "POST",
    body: data,
  });
}

export async function regenerateMfaRecoveryCodes(data: {
  password: string;
  mfaCode: string;
}): Promise<string[]> {
  const response = await apiRequest<{ recoveryCodes: string[] }>(
    "/api/v1/admin/auth/mfa/recovery-codes/regenerate",
    {
      method: "POST",
      body: data,
    },
  );
  return response.recoveryCodes;
}

export async function logout(): Promise<void> {
  await apiRequest<{ status: string }>("/api/v1/admin/auth/logout", {
    method: "POST",
  });
}

export async function listUsers(): Promise<AdminUser[]> {
  const users = await apiRequest<AdminUser[]>("/api/v1/admin/users/");
  return users.map(mapAdminUser);
}

export async function createUser(data: {
  email: string;
  name: string;
  password: string;
  role: UserRole;
}): Promise<AdminUser> {
  const user = await apiRequest<AdminUser>("/api/v1/admin/users/", {
    method: "POST",
    body: {
      email: data.email,
      name: data.name,
      password: data.password,
      role: data.role,
    },
  });
  return mapAdminUser(user);
}

export async function updateUser(
  id: string,
  data: Partial<Pick<AuthUser, "name" | "role">>,
): Promise<AdminUser> {
  const user = await apiRequest<AdminUser>(`/api/v1/admin/users/${id}`, {
    method: "PATCH",
    body: { name: data.name, role: data.role },
  });
  return mapAdminUser(user);
}

export async function deleteUser(id: string): Promise<void> {
  await apiRequest<{ status: string }>(`/api/v1/admin/users/${id}`, {
    method: "DELETE",
  });
}

export async function resetUserMfa(
  id: string,
  data: { adminPassword: string; adminMfaCode: string },
): Promise<void> {
  await apiRequest<{ status: string }>(`/api/v1/admin/users/${id}/mfa/reset`, {
    method: "POST",
    body: data,
  });
}

export async function listAdminUserSessions(
  id: string,
): Promise<AdminUserSession[]> {
  return apiRequest<AdminUserSession[]>(`/api/v1/admin/users/${id}/sessions`);
}

export async function revokeAdminUserSession(
  userId: string,
  sessionId: string,
): Promise<void> {
  await apiRequest<{ status: string }>(
    `/api/v1/admin/users/${userId}/sessions/${sessionId}`,
    { method: "DELETE" },
  );
}

export async function revokeAdminUserSessions(userId: string): Promise<void> {
  await apiRequest<{ status: string }>(
    `/api/v1/admin/users/${userId}/sessions`,
    { method: "DELETE" },
  );
}

export async function getDashboard(): Promise<DashboardPayload> {
  return apiRequest<DashboardPayload>("/api/v1/admin/dashboard/");
}

export async function getDashboardOperations(): Promise<DashboardOperationsPayload> {
  return apiRequest<DashboardOperationsPayload>(
    "/api/v1/admin/dashboard/operations",
  );
}

export async function listAppUpdateConfigs(): Promise<AppUpdateConfig[]> {
  return apiRequest<AppUpdateConfig[]>("/api/v1/admin/app-updates/");
}

export async function createAppUpdateConfig(
  data: Omit<
    AppUpdateConfig,
    "id" | "updatedAt" | "updatedBy" | "checks24h" | "rolloutHitRate"
  >,
): Promise<AppUpdateConfig> {
  return apiRequest<AppUpdateConfig>("/api/v1/admin/app-updates/", {
    method: "POST",
    body: data,
  });
}

export async function updateAppUpdateConfig(
  id: string,
  data: Omit<
    AppUpdateConfig,
    "id" | "updatedAt" | "updatedBy" | "checks24h" | "rolloutHitRate"
  >,
): Promise<AppUpdateConfig> {
  return apiRequest<AppUpdateConfig>(`/api/v1/admin/app-updates/${id}`, {
    method: "PUT",
    body: data,
  });
}

export async function toggleAppUpdateConfig(
  id: string,
): Promise<AppUpdateConfig> {
  return apiRequest<AppUpdateConfig>(`/api/v1/admin/app-updates/${id}/toggle`, {
    method: "POST",
  });
}

export async function applyAppUpdateQuickAction(
  id: string,
  data: AppUpdateQuickActionPayload,
): Promise<AppUpdateConfig> {
  return apiRequest<AppUpdateConfig>(
    `/api/v1/admin/app-updates/${id}/quick-actions`,
    {
      method: "POST",
      body: data,
    },
  );
}

export async function previewAppUpdateDecision(data: {
  platform: Platform;
  currentVersion: string;
  deviceId: string;
  customerId: string;
  channel: string;
}): Promise<AppUpdatePreviewResult> {
  return apiRequest<AppUpdatePreviewResult>(
    "/api/v1/admin/app-updates/preview",
    { method: "POST", body: data },
  );
}

export async function getAppUpdateAnalytics(): Promise<{
  checksTrend7d: Array<{ date: string; ios: number; android: number }>;
}> {
  return apiRequest<{
    checksTrend7d: Array<{ date: string; ios: number; android: number }>;
  }>("/api/v1/admin/app-updates/analytics");
}

export async function getAppUpdateReleaseSummary(): Promise<AppReleaseSummary> {
  return apiRequest<AppReleaseSummary>(
    "/api/v1/admin/app-updates/release-summary",
  );
}

export async function listPaymentMethodConfigs(): Promise<
  PaymentMethodConfig[]
> {
  return apiRequest<PaymentMethodConfig[]>("/api/v1/admin/payment-methods/");
}

export async function createPaymentMethodConfig(
  data: PaymentMethodConfigPayload,
): Promise<PaymentMethodConfig> {
  return apiRequest<PaymentMethodConfig>("/api/v1/admin/payment-methods/", {
    method: "POST",
    body: data,
  });
}

export async function updatePaymentMethodConfig(
  id: string,
  data: PaymentMethodConfigPayload,
): Promise<PaymentMethodConfig> {
  return apiRequest<PaymentMethodConfig>(
    `/api/v1/admin/payment-methods/${id}`,
    { method: "PUT", body: data },
  );
}

export async function togglePaymentMethodConfig(
  id: string,
): Promise<PaymentMethodConfig> {
  return apiRequest<PaymentMethodConfig>(
    `/api/v1/admin/payment-methods/${id}/toggle`,
    { method: "POST" },
  );
}

export async function listFeatureFlagConfigs(): Promise<FeatureFlagConfig[]> {
  return apiRequest<FeatureFlagConfig[]>("/api/v1/admin/feature-flags/");
}

export async function createFeatureFlagConfig(
  data: FeatureFlagConfigPayload,
): Promise<FeatureFlagConfig> {
  return apiRequest<FeatureFlagConfig>("/api/v1/admin/feature-flags/", {
    method: "POST",
    body: data,
  });
}

export async function updateFeatureFlagConfig(
  id: string,
  data: FeatureFlagConfigPayload,
): Promise<FeatureFlagConfig> {
  return apiRequest<FeatureFlagConfig>(`/api/v1/admin/feature-flags/${id}`, {
    method: "PUT",
    body: data,
  });
}

export async function toggleFeatureFlagConfig(
  id: string,
): Promise<FeatureFlagConfig> {
  return apiRequest<FeatureFlagConfig>(
    `/api/v1/admin/feature-flags/${id}/toggle`,
    { method: "POST" },
  );
}

export async function previewFeatureFlagDecision(data: {
  key: string;
  platform: Platform;
  customerId?: string | null;
  deviceId?: string | null;
}): Promise<FeatureFlagPreviewResult> {
  return apiRequest<FeatureFlagPreviewResult>(
    "/api/v1/admin/feature-flags/preview",
    { method: "POST", body: data },
  );
}

export async function listBuyerServiceFaqItems(): Promise<
  BuyerServiceFaqItem[]
> {
  return apiRequest<BuyerServiceFaqItem[]>("/api/v1/admin/buyer-service/faq/");
}

export async function createBuyerServiceFaqItem(
  data: BuyerServiceFaqItemPayload,
): Promise<BuyerServiceFaqItem> {
  return apiRequest<BuyerServiceFaqItem>("/api/v1/admin/buyer-service/faq/", {
    method: "POST",
    body: data,
  });
}

export async function updateBuyerServiceFaqItem(
  id: string,
  data: BuyerServiceFaqItemPayload,
): Promise<BuyerServiceFaqItem> {
  return apiRequest<BuyerServiceFaqItem>(
    `/api/v1/admin/buyer-service/faq/${id}`,
    { method: "PUT", body: data },
  );
}

export async function toggleBuyerServiceFaqItem(
  id: string,
): Promise<BuyerServiceFaqItem> {
  return apiRequest<BuyerServiceFaqItem>(
    `/api/v1/admin/buyer-service/faq/${id}/toggle`,
    { method: "POST" },
  );
}

export async function reorderBuyerServiceFaqItems(
  itemIds: string[],
): Promise<BuyerServiceFaqItem[]> {
  return apiRequest<BuyerServiceFaqItem[]>(
    "/api/v1/admin/buyer-service/faq/reorder",
    {
      method: "POST",
      body: { itemIds },
    },
  );
}

export async function deleteBuyerServiceFaqItem(id: string): Promise<void> {
  await apiRequest<{ success: boolean }>(
    `/api/v1/admin/buyer-service/faq/${id}`,
    { method: "DELETE" },
  );
}

export interface DeviceFilters {
  /** Exact match (backend filters by equality). */
  customerId?: string;
  /** Exact match. */
  deviceId?: string;
  platform?: string;
  status?: DeviceStatus;
  limit?: number;
}

export async function listDevices(filters: DeviceFilters = {}): Promise<DeviceInstallation[]> {
  const query = buildQuery({
    customer_id: filters.customerId,
    device_id: filters.deviceId,
    platform: filters.platform,
    status: filters.status,
    limit: filters.limit,
  });
  const payload = await apiRequest<{
    items: Array<{
      id: string;
      deviceId: string;
      customerId: string | null;
      platform: Platform;
      appVersion: string | null;
      buildNumber: string | null;
      pushPermission: boolean;
      status: DeviceStatus;
      lastSeen: string;
      registeredAt: string;
      invalidatedAt: string | null;
      invalidReason: string | null;
      maskedPushToken: string;
      tokenHash: string;
    }>;
  }>(`/api/v1/admin/devices/${query}`);

  return payload.items.map((item) => ({
    installationId: item.id,
    deviceId: item.deviceId,
    customerId: item.customerId ?? "",
    platform: item.platform,
    appVersion: item.appVersion ?? "—",
    buildNumber: item.buildNumber ?? "—",
    pushPermission: item.pushPermission,
    status: item.status,
    lastSeen: item.lastSeen,
    registeredAt: item.registeredAt,
    invalidatedAt: item.invalidatedAt,
    invalidReason: item.invalidReason,
    maskedPushToken: item.maskedPushToken,
    tokenHash: item.tokenHash,
  }));
}

export async function getDevicesAnalytics(): Promise<DevicesAnalyticsPayload> {
  return apiRequest<DevicesAnalyticsPayload>("/api/v1/admin/devices/analytics");
}

export async function revokeDevice(
  installationId: string,
  reason = "manual_revoke",
): Promise<DeviceInstallation> {
  const item = await apiRequest<{
    id: string;
    deviceId: string;
    customerId: string | null;
    platform: Platform;
    appVersion: string | null;
    buildNumber: string | null;
    pushPermission: boolean;
    status: DeviceStatus;
    lastSeen: string;
    registeredAt: string;
    invalidatedAt: string | null;
    invalidReason: string | null;
    maskedPushToken: string;
    tokenHash: string;
  }>(`/api/v1/admin/devices/${installationId}/revoke`, {
    method: "POST",
    body: { reason },
  });

  return {
    installationId: item.id,
    deviceId: item.deviceId,
    customerId: item.customerId ?? "",
    platform: item.platform,
    appVersion: item.appVersion ?? "—",
    buildNumber: item.buildNumber ?? "—",
    pushPermission: item.pushPermission,
    status: item.status,
    lastSeen: item.lastSeen,
    registeredAt: item.registeredAt,
    invalidatedAt: item.invalidatedAt,
    invalidReason: item.invalidReason,
    maskedPushToken: item.maskedPushToken,
    tokenHash: item.tokenHash,
  };
}

export async function previewPushRecipients(
  customerId?: string | null,
): Promise<PushPreviewResult> {
  return apiRequest<PushPreviewResult>("/api/v1/admin/push/preview", {
    method: "POST",
    body: {
      customerId:
        customerId && customerId.trim().length > 0 ? customerId : null,
    },
  });
}

export async function sendTestPush(data: {
  customerId?: string | null;
  eventType: string;
  title: string;
  body: string;
  data?: Record<string, string> | null;
}): Promise<PushTestResult> {
  return apiRequest<PushTestResult>("/api/v1/admin/push/test", {
    method: "POST",
    body: {
      ...data,
      customerId:
        data.customerId && data.customerId.trim().length > 0
          ? data.customerId
          : null,
    },
  });
}

export async function listPushCampaigns(): Promise<PushCampaign[]> {
  return apiRequest<PushCampaign[]>("/api/v1/admin/push/campaigns");
}

export async function createPushCampaign(
  data: PushCampaignPayload,
): Promise<PushCampaign> {
  return apiRequest<PushCampaign>("/api/v1/admin/push/campaigns", {
    method: "POST",
    body: data,
  });
}

export async function dryRunPushCampaign(
  id: string,
): Promise<PushCampaignDryRunResult> {
  return apiRequest<PushCampaignDryRunResult>(
    `/api/v1/admin/push/campaigns/${id}/dry-run`,
    { method: "POST" },
  );
}

export async function sendPushCampaign(
  id: string,
): Promise<PushCampaignSendResult> {
  return apiRequest<PushCampaignSendResult>(
    `/api/v1/admin/push/campaigns/${id}/send`,
    { method: "POST" },
  );
}

export async function listPushCampaignDeliveries(
  id: string,
): Promise<DeliveryLog[]> {
  return apiRequest<DeliveryLog[]>(
    `/api/v1/admin/push/campaigns/${id}/deliveries`,
  );
}

export async function listPushDeliveries(): Promise<DeliveryLog[]> {
  return apiRequest<DeliveryLog[]>("/api/v1/admin/push/deliveries");
}

export async function listPushFailures(): Promise<DeliveryLog[]> {
  return apiRequest<DeliveryLog[]>("/api/v1/admin/push/failures");
}

export async function getPushAnalytics(): Promise<PushAnalyticsPayload> {
  return apiRequest<PushAnalyticsPayload>("/api/v1/admin/push/analytics");
}

export async function searchTimelineEvents(
  filters: TimelineFilters = {},
): Promise<TimelineSearchResponse> {
  const query = buildQuery({
    event_type: filters.eventType,
    actor_type: filters.actorType,
    actor_id: filters.actorId,
    severity: filters.severity,
    platform: filters.platform,
    customer_id: filters.customerId,
    device_id: filters.deviceId,
    resource_type: filters.resourceType,
    resource_id: filters.resourceId,
    created_from: filters.createdFrom,
    created_to: filters.createdTo,
    limit: filters.limit ?? 50,
    offset: filters.offset ?? 0,
  });
  return apiRequest<TimelineSearchResponse>(
    `/api/v1/admin/timeline/search${query}`,
  );
}

export async function getTimelineEvent(id: string): Promise<TimelineEvent> {
  return apiRequest<TimelineEvent>(`/api/v1/admin/timeline/events/${id}`);
}

export async function searchAirbridgeTrackingLinks(
  filters: AirbridgeTrackingLinkFilters = {},
): Promise<AirbridgeTrackingLinkSearchResponse> {
  const query = buildQuery({
    status: filters.status,
    source_service: filters.sourceService,
    channel: filters.channel,
    search: filters.search,
    created_from: filters.createdFrom,
    created_to: filters.createdTo,
    limit: filters.limit ?? 50,
    offset: filters.offset ?? 0,
  });
  return apiRequest<AirbridgeTrackingLinkSearchResponse>(
    `/api/v1/admin/airbridge-links/${query}`,
  );
}

export async function getAirbridgeTrackingLink(
  id: string,
): Promise<AirbridgeTrackingLink> {
  return apiRequest<AirbridgeTrackingLink>(
    `/api/v1/admin/airbridge-links/${id}`,
  );
}

export async function recordAirbridgeTrackingLinkCopy(
  id: string,
  field: AirbridgeTrackingLinkCopyField,
): Promise<void> {
  await apiRequest<{ status: string }>(
    `/api/v1/admin/airbridge-links/${id}/copy-events`,
    {
      method: "POST",
      body: { field },
    },
  );
}

export async function getSystem(): Promise<SystemPayload> {
  return apiRequest<SystemPayload>("/api/v1/admin/system/");
}

export async function listSupportTraces(
  filters: SupportTraceFilters = {},
): Promise<SupportTracesResponse> {
  const query = buildQuery({
    customer_id: filters.customerId,
    session_id: filters.sessionId,
    lead_id: filters.leadId,
    source: filters.source,
    stage: filters.stage,
    decision: filters.decision,
    outcome: filters.outcome,
    from_: filters.from,
    to: filters.to,
    offset: filters.offset ?? 0,
    limit: filters.limit ?? 50,
  });
  return apiRequest<SupportTracesResponse>(
    `/api/v1/admin/support-traces/${query}`,
  );
}

export async function getSupportTrace(
  traceId: string,
  limit = 1000,
): Promise<SupportTraceDetailResponse> {
  const query = buildQuery({ limit });
  return apiRequest<SupportTraceDetailResponse>(
    `/api/v1/admin/support-traces/${traceId}${query}`,
  );
}

export async function getSupportTraceAnalytics(
  filters: SupportTraceFilters = {},
): Promise<SupportTraceAnalyticsResponse> {
  const query = buildQuery({
    from_: filters.from,
    to: filters.to,
    customer_id: filters.customerId,
    source: filters.source,
    limit: 100,
  });
  return apiRequest<SupportTraceAnalyticsResponse>(
    `/api/v1/admin/support-traces/analytics${query}`,
  );
}

function mapAdminUser(user: AdminUser): AdminUser {
  return {
    ...user,
    name: user.name || user.fullName,
    fullName: user.fullName || user.name,
    createdAt: user.createdAt,
    updatedAt: user.updatedAt,
    deletedAt: user.deletedAt,
    isActive: user.isActive,
    mfaEnabled: user.mfaEnabled ?? false,
    mfaEnabledAt: user.mfaEnabledAt ?? null,
    recoveryCodesRemaining: user.recoveryCodesRemaining ?? 0,
  };
}

function buildQuery(
  params: Record<string, string | number | null | undefined>,
): string {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") return;
    query.set(key, String(value));
  });
  const value = query.toString();
  return value ? `?${value}` : "";
}
